/**
 * SharedItemView Page
 * 
 * Behavior:
 * - If user is logged in: Redirect to inventory with item highlighted
 * - If guest: Show interactive guest view similar to GuestCollectionView
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Edit2, MapPin, Package, Clock } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { getSharedItemByToken, SharedItemData, claimPendingShares, updateSharedItem } from '../services/sharingService';
import { getColorFromName, getInitials } from '../utils/imageHelpers';
import SharedCollectionHeader from '../components/SharedCollectionHeader';
import { UnifiedItemEditModal, EditableItem } from '../components/itemEdit';
import '../styles/pages/sharedItemView.css';

const SharedItemView: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { token: authToken, user } = useAuth();
  
  const [sharedItem, setSharedItem] = useState<SharedItemData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  
  // Edit modal state
  const [showEditModal, setShowEditModal] = useState(false);
  const [editItem, setEditItem] = useState<EditableItem | null>(null);

  useEffect(() => {
    if (token) {
      loadSharedItem();
    }
  }, [token]);

  // If user is logged in, claim the share and redirect to inventory
  useEffect(() => {
    if (authToken && sharedItem && user) {
      // Claim any pending shares and redirect
      claimPendingShares(authToken).then(() => {
        navigate('/inventory', { 
          state: { 
            highlightItemId: sharedItem.item.id,
            filterCollection: 'shared_with_me'
          }
        });
      }).catch(console.error);
    }
  }, [authToken, sharedItem, user, navigate]);

  const loadSharedItem = async () => {
    if (!token) return;
    
    try {
      setLoading(true);
      setError(null);
      const data = await getSharedItemByToken(token);
      setSharedItem(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load shared item');
    } finally {
      setLoading(false);
    }
  };

  const handleEditItem = () => {
    if (!sharedItem || sharedItem.permission !== 'edit') return;
    
    setEditItem({
      id: sharedItem.item.id,
      name: sharedItem.item.name,
      description: sharedItem.item.description || '',
      categories: [],
      color: '',
      area: sharedItem.item.area || '',
      location: sharedItem.item.location || '',
      sub_location: sharedItem.item.sub_location || '',
      quantity: sharedItem.item.quantity || 1,
      quantity_min: 1,
      is_grocery: sharedItem.item.is_grocery,
      uit_id: sharedItem.item.uit_id,
      unit: sharedItem.item.unit,
      is_checked: sharedItem.item.is_checked,
    });
    setShowEditModal(true);
  };

  const handleSaveEdit = async () => {
    if (!token || !editItem || !sharedItem) return;
    
    try {
      setSaving(true);
      await updateSharedItem(token, {
        name: editItem.name,
        description: editItem.description,
        area: editItem.area,
        location: editItem.location,
        sub_location: editItem.sub_location,
        quantity: editItem.quantity,
        is_grocery: editItem.is_grocery,
        uit_id: editItem.uit_id,
        unit: editItem.unit,
        is_checked: editItem.is_checked,
      });
      
      // Reload the item
      await loadSharedItem();
      setShowEditModal(false);
      setEditItem(null);
    } catch (err: any) {
      console.error('Failed to save:', err);
      alert(err.message || 'Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  const handleLogin = () => {
    sessionStorage.setItem('pendingShareToken', token || '');
    navigate('/login', { state: { from: `/s/${token}` } });
  };

  const handleSignUp = () => {
    sessionStorage.setItem('pendingShareToken', token || '');
    navigate('/signup', { state: { from: `/s/${token}` } });
  };

  // Loading state
  if (loading) {
    return (
      <div className="shared-item-view">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading shared item...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="shared-item-view">
        <div className="error-container">
          <h2>Unable to load item</h2>
          <p>{error}</p>
          <button onClick={() => navigate('/')} className="back-button">
            <ArrowLeft size={18} />
            Go to Wandering Sock
          </button>
        </div>
      </div>
    );
  }

  if (!sharedItem) {
    return null;
  }

  // If logged in, show redirect message (useEffect will handle actual redirect)
  if (authToken) {
    return (
      <div className="shared-item-view">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Redirecting to your inventory...</p>
        </div>
      </div>
    );
  }

  const { item, sharer, message, shared_at, permission, recipient_is_user } = sharedItem;
  const hasImage = item.image_url || item.thumbnail_url;
  const locationParts = [item.area, item.location, item.sub_location].filter(Boolean);
  const canEdit = permission === 'edit';

  // If recipient is a registered user, show login prompt instead of guest view
  if (recipient_is_user) {
    return (
      <div className="shared-item-view">
        <header className="shared-item-header">
          <div className="brand">
            <img src="/sock-icon.png" alt="Wandering Sock" className="brand-icon" />
            <span className="brand-name">Wandering Sock</span>
          </div>
        </header>
        
        <main className="shared-item-content">
          <div className="login-prompt-card">
            <div 
              className="sharer-avatar"
              style={{ backgroundColor: getColorFromName(sharer.name) }}
            >
              {getInitials(sharer.name)}
            </div>
            
            <h2>{sharer.name} shared an item with you</h2>
            
            {message && (
              <div className="share-message">
                <p>"{message}"</p>
              </div>
            )}
            
            <div className="item-preview">
              <strong>{item.name}</strong>
              {item.description && <p>{item.description}</p>}
            </div>
            
            <p className="login-prompt-text">
              Log in to view this item in your inventory
            </p>
            
            <div className="cta-buttons">
              <button className="cta-button primary" onClick={handleLogin}>
                Log In
              </button>
            </div>
            
            <p className="signup-link">
              Don't have an account? <button onClick={handleSignUp}>Sign up free</button>
            </p>
          </div>
        </main>
      </div>
    );
  }

  // Guest view for non-registered recipients
  return (
    <div className="shared-item-view guest-view">
      {/* Header - similar to GuestCollectionView */}
      <SharedCollectionHeader
        collectionName={`Shared by ${sharer.name}`}
        collectionDescription={message || undefined}
        itemCount={1}
      />

      {/* Main Content */}
      <main className="shared-item-content">
        {/* Item Card - Large, centered display */}
        <div className="guest-item-card" onClick={canEdit ? handleEditItem : undefined}>
          <div className="item-image-container">
            {hasImage ? (
              <img 
                src={item.image_url || item.thumbnail_url || ''} 
                alt={item.name}
                className="item-image"
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.style.display = 'none';
                  target.parentElement?.classList.add('no-image');
                }}
              />
            ) : (
              <div 
                className="item-placeholder"
                style={{ backgroundColor: getColorFromName(item.name) }}
              >
                <span className="item-initials">{getInitials(item.name)}</span>
              </div>
            )}
            
            {canEdit && (
              <button className="edit-overlay" onClick={handleEditItem}>
                <Edit2 size={24} />
                <span>Tap to edit</span>
              </button>
            )}
          </div>
          
          <div className="item-info">
            <h1 className="item-name">{item.name}</h1>
            
            {item.description && (
              <p className="item-description">{item.description}</p>
            )}
            
            <div className="item-meta">
              {locationParts.length > 0 && (
                <div className="meta-item">
                  <MapPin size={16} />
                  <span>{locationParts.join(' › ')}</span>
                </div>
              )}
              
              {item.quantity && item.quantity > 1 && (
                <div className="meta-item">
                  <Package size={16} />
                  <span>Qty: {item.quantity}</span>
                </div>
              )}
              
              {shared_at && (
                <div className="meta-item">
                  <Clock size={16} />
                  <span>Shared {new Date(shared_at).toLocaleDateString()}</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Permission indicator */}
        <div className="permission-banner">
          <span className={`permission-badge ${permission}`}>
            {permission === 'view' ? 'View only' : 'You can edit this item'}
          </span>
        </div>

        {/* Sign up prompt */}
        <div className="guest-cta">
          <p>Want to save this to your own inventory?</p>
          <div className="cta-buttons">
            <button className="cta-button primary" onClick={handleSignUp}>
              Sign Up Free
            </button>
            <button className="cta-button secondary" onClick={handleLogin}>
              Log In
            </button>
          </div>
        </div>
      </main>

      {/* Edit Modal */}
      {showEditModal && editItem && (
        <UnifiedItemEditModal
          open={showEditModal}
          item={editItem}
          onClose={() => {
            setShowEditModal(false);
            setEditItem(null);
          }}
          onChange={(patch) => {
            setEditItem(prev => prev ? { ...prev, ...patch } : null);
          }}
          onSave={handleSaveEdit}
          mode="guest"
          saving={saving}
          saveDisabled={!editItem.name.trim()}
        />
      )}
    </div>
  );
};

export default SharedItemView;
